import { useState, useEffect, useRef } from 'react';
import { useApp } from '../AppContext';
import { onboardingApi } from '../services/api';
import Chip from '../components/Chip';
import cFirstLogo from '../images/SwiftCheqLogo.png';

const LICLASS_TO_TAB = { 1: 0, 3: 1, 4: 2, 7: 3 };

const TABS = [
  { label: 'Client Contact Details',  icon: '👤' },
  { label: 'Billing Details',         icon: '🧾' },
  { label: 'Packages & Pricing',     icon: '📦' },
  { label: 'Agreement / SLA',         icon: '📜' },
];

const EMPLOYEE_SIZES = ['', '0-100', '201-500', '501-1,000', '1,001-5,000', '5,001-10,000', '10,000+'];

const PRICING_SUB_TABS = ['🛍️ A la Carte', '📦 Package', '➕ Extra Checks'];

const CONTRACT_ATTACHMENTS = ['Agreement Copy', 'PAN Copy', 'TAN Copy', 'GST Copy'];

const NOT_APPLICABLE_VALUE = 'UNREGISTERED';

const onlyDigits = e => { if (!/[0-9]/.test(e.key)) e.preventDefault(); };

function deriveCategory(checktype) {
  const n = (checktype || '').toLowerCase().trim();
  if (n.includes('address'))          return 'Address';
  if (n.startsWith('alcohol'))        return 'Alcohol';
  if (n.startsWith('criminal'))       return 'Criminal';
  if (n.startsWith('credential'))     return 'Credential';
  if (n.startsWith('database'))       return 'Database';
  if (n.startsWith('drug'))           return 'Drug Test';
  if (n === 'education')              return 'Education';
  if (n === 'directorship' || n.startsWith('employment') ||
      n === 'gap check'    || n === 'mca'     || n === 'psychometric' ||
      n === 'reference'    || n === 'resume'  ||
      n === 'right to work' || n === 'work authorization') return 'Employment';
  if (n === 'adverse media' || n === 'bank statement' || n.startsWith('bankruptcy') ||
      n === 'credit'        || n.startsWith('financial') ||
      n === 'form 16'       || n === 'form 26as')          return 'Finance';
  if (n.startsWith('identity') || n === 'uan' || n.startsWith('pan to')) return 'Identity';
  return 'Other';
}

function getCheckTypeSubLabel(checktype, category) {
  const ct = (checktype || '').trim();
  const cat = (category || '').trim();
  if (!ct || ct.toLowerCase() === cat.toLowerCase()) return null;
  const esc = cat.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  const stripped = ct.replace(new RegExp(`^${esc}\\s*`, 'i'), '')
                      .replace(new RegExp(`\\s*${esc}$`, 'i'), '')
                      .trim();
  return stripped || ct;
}

function catChipClass(cat) {
  const map = {
    Address: 'cn', Alcohol: 'ca', Criminal: 'cr', Credential: 'cb',
    Database: 'cb', 'Drug Test': 'cpur', Education: 'cg', Employment: 'cg',
    Finance: 'cr', Identity: 'cb', Other: 'cgr',
  };
  return map[cat] || 'cn';
}

function emptyContact() {
  return { id: 0, contactType: '', contactName: '', designation: '', email: '', phone: '', mobile: '' };
}

export default function ClientOnBoarding({ onNavigate, liclass = 1, clientId: initClientId = '', allDone = false, status = '' }) {
  const { addToast, user, logout } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    function handleOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [menuOpen]);

  function handleLogout() {
    logout();
    onNavigate('login');
    setMenuOpen(false);
  }

  const companyName = user?.clientname || user?.client_name || user?.companyname || user?.name || user?.username || 'Account';
  const email       = user?.email || user?.email_address || user?.emailaddress || user?.username || '';
  const initials    = companyName.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'U';
  const [activeTab, setActiveTab]     = useState(() => {
    const stored = localStorage.getItem('onb_activeTab');
    if (stored !== null) return Number(stored);
    return LICLASS_TO_TAB[liclass] ?? 0;
  });
  const [clientId, setClientId]       = useState(String(initClientId || localStorage.getItem('onb_clientId') || ''));
  const [apiData, setApiData]         = useState({});
  const [loading, setLoading]         = useState(true);
  const [submitting, setSubmitting]   = useState(false);
  const [pageMsg, setPageMsg]         = useState({ type: '', text: '' });

  // ── Tab 1 ──────────────────────────────────────────────────
  const [t1, setT1] = useState({
    clientName: '',ClientId : '', cinNumber: '', country: '', state: '',
    city: '', zipCode: '', industryType: '', employeeSize: '',
  });
  const [contacts, setContacts] = useState([]);

  // ── Tab 2 ──────────────────────────────────────────────────
  const [t2, setT2] = useState({
    attnPersonName: '', department: '',
    billAddr1: '', billAddr2: '', billAddr3: '', billAddr4: '',
    billCountry: '', billState: '', billCity: '', billPostalCode: '',
    billGstin: '', billPan: '', billTan: '',
    sameAsBill: false,
    shipAddr1: '', shipAddr2: '', shipAddr3: '', shipAddr4: '',
    shipCountry: '', shipState: '', shipCity: '', shipPostalCode: '',
    shipGstin: '', shipPan: '', shipTan: '',
    billingAction: '',
  });
  const [billNA, setBillNA]   = useState({ gstin: false, pan: false, tan: false });
  const [shipNA, setShipNA]   = useState({ gstin: false, pan: false, tan: false });
  const [t2Errors, setT2Errors] = useState({});

  // ── Country / State lists ───────────────────────────────────
  const [countries,  setCountries]  = useState([]);
  const [t1States,   setT1States]   = useState([]);
  const [billStates, setBillStates] = useState([]);
  const [shipStates, setShipStates] = useState([]);

  // ── Tab 3 ──────────────────────────────────────────────────
  const [pricingSubTab,      setPricingSubTab]      = useState(0);
  const [pricingAlacarte,    setPricingAlacarte]    = useState([]);
  const [pricingPackages,    setPricingPackages]    = useState([]);
  const [pricingExtraChecks, setPricingExtraChecks] = useState([]);
  const [checkTypeSettings,  setCheckTypeSettings]  = useState([]);
  const [alacarteSearch,     setAlacarteSearch]     = useState('');
  const [alacarteCategory,   setAlacarteCategory]   = useState('');

  // ── Tab 4 ──────────────────────────────────────────────────
  const [t4, setT4] = useState({
    contractId: '', contractType: '', startDate: '', endDate: '', remarks: '', contractAction: 'Add',
    hdncontractcopy: '', hdncontractcopyoriginal: '', hdncontractcopyext: '',
  });
  const [attachmentFiles,     setAttachmentFiles]     = useState({});
  const [attachmentUpload,    setAttachmentUpload]    = useState({});
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [downloading, setDownloading]                 = useState(false);
  const [completed,   setCompleted]                   = useState(allDone);

  function goToLoginOnError() {
    addToast('Something went wrong. Please try again later.', 'error');
    onNavigate('login');
  }

  useEffect(() => {
    if (initClientId) localStorage.setItem('onb_clientId', String(initClientId));
    loadTab(activeTab);
    onboardingApi.getCountryList()
      .then(data => { if (Array.isArray(data)) setCountries(data); })
      .catch(goToLoginOnError);
  }, []);

  useEffect(() => {
    if (!t1.country) { setT1States([]); return; }
    onboardingApi.getStateList(t1.country)
      .then(data => { if (Array.isArray(data)) setT1States(data); })
      .catch(() => {});
  }, [t1.country]);

  useEffect(() => {
    if (!t2.billCountry) { setBillStates([]); return; }
    onboardingApi.getStateList(t2.billCountry)
      .then(data => { if (Array.isArray(data)) setBillStates(data); })
      .catch(() => {});
  }, [t2.billCountry]);

  useEffect(() => {
    if (!t2.shipCountry) { setShipStates([]); return; }
    onboardingApi.getStateList(t2.shipCountry)
      .then(data => { if (Array.isArray(data)) setShipStates(data); })
      .catch(() => {});
  }, [t2.shipCountry]);

  function gotoTab(i) {
    setActiveTab(i);
    localStorage.setItem('onb_activeTab', String(i));
  }

  function loadTab(tabIndex, showLoader = true) {
    const lc = Number(Object.keys(LICLASS_TO_TAB).find(k => LICLASS_TO_TAB[k] === tabIndex)) || liclass;
    if (showLoader) setLoading(true);
    onboardingApi.getDetails(lc)
      .then(res => {
        const dataIsNull = res?.data == null;
        const d = res?.data || {};
        setApiData(d);
        prefill(d, dataIsNull, lc);
        gotoTab(tabIndex);
      })
      .catch(goToLoginOnError)
      .finally(() => { if (showLoader) setLoading(false); });
  }

  const DEFAULT_CONTACTS = [
    { id: 0, contactType: 'Client POC',               contactName: '', designation: '', email: '', phone: '', mobile: '' },
    { id: 0, contactType: 'Client Finance POC',       contactName: '', designation: '', email: '', phone: '', mobile: '' },
    { id: 0, contactType: 'Business Person',          contactName: '', designation: '', email: '', phone: '', mobile: '' },
    { id: 0, contactType: 'Client Signing Authority', contactName: '', designation: '', email: '', phone: '', mobile: '' },
  ];

  function prefill(d, dataIsNull, lc) {
    if (lc === 1) {
      const cl = d.clients?.[0];
      if (cl) {
        setT1({
          clientName:   cl.clientname   || '',
          clientId:     cl.clientid     || '',
          cinNumber:    cl.cinnumber    || '',
          country:      cl.country      || '',
          state:        cl.state        || '',
          city:         cl.city         || '',
          zipCode:      cl.zipcode      || '',
          industryType: cl.industrytype || '',
          employeeSize: cl.employeesize || '',
        });
      }
      const savedContacts = (d.contactssingle?.contacts || []).filter(c => c.id > 0);
      if (dataIsNull) {
        setContacts([emptyContact(), ...DEFAULT_CONTACTS]);
      } else if (savedContacts.length) {
        setContacts([
          ...savedContacts.map(c => ({
            id:          c.id          || 0,
            contactType: c.contacttype || '',
            contactName: c.contactname || '',
            designation: c.designation || '',
            email:       c.email       || '',
            phone:       c.phone       || '',
            mobile:      c.mobile      || '',
          })),
          ...DEFAULT_CONTACTS,
        ]);
      } else {
        setContacts([...DEFAULT_CONTACTS]);
      }
    }

    if (lc === 3) {
      const b  = d.clientbillinginfomodel;
      const pv = d.pantanvalidationvalue || '';
      if (b) {
        setT2(prev => ({
          ...prev,
          attnPersonName: b.attnpersonname  || '',
          department:     b.department      || '',
          billAddr1:      b.billtoaddress1  || '',
          billAddr2:      b.billtoaddress2  || '',
          billAddr3:      b.billtoaddress3  || '',
          billAddr4:      b.billtoaddress4  || '',
          billCountry:    b.billcountry     || '',
          billState:      b.billstate       || '',
          billCity:       b.billcity        || '',
          billPostalCode: b.billpostalcode  || '',
          billGstin:      b.gstinofbill     || '',
          billPan:        b.panofbill       || '',
          billTan:        b.tanofbill       || '',
          shipAddr1:      b.shiptoaddress1  || '',
          shipAddr2:      b.shiptoaddress2  || '',
          shipAddr3:      b.shiptoaddress3  || '',
          shipAddr4:      b.shiptoaddress4  || '',
          shipCountry:    b.shipcountry     || '',
          shipState:      b.shipstate       || '',
          shipCity:       b.shipcity        || '',
          shipPostalCode: b.shippostalcode  || '',
          shipGstin:      b.gstinofship     || '',
          shipPan:        b.panofship       || '',
          shipTan:        b.tanofship       || '',
          billingAction: 'Update',
        }));
        const isNA = v => v === NOT_APPLICABLE_VALUE || (!!pv && v === pv);
        setBillNA({ gstin: isNA(b.gstinofbill), pan: isNA(b.panofbill), tan: isNA(b.tanofbill) });
        setShipNA({ gstin: isNA(b.gstinofship), pan: isNA(b.panofship), tan: isNA(b.tanofship) });
      }
    }

    if (lc === 7) {
      if (!dataIsNull) {
        const con = d.clientcontractmodel;
        if (con) {
          setT4(prev => ({
            ...prev,
            contractId:     con.id           || '',
            contractType:   con.contracttype || '',
            startDate:      con.startdate    || '',
            endDate:        con.enddate      || '',
            remarks:        con.remarks      || '',
            contractAction: 'Update',
          }));
          setExistingAttachments(con.attachment || []);
        }
      }
    }
    if (lc === 4) {
      setPricingAlacarte(d.pricinglist || []);
      setPricingPackages(d.pricinglistforpackage || []);
      setPricingExtraChecks(d.pricinglistforextrachecks || []);
      setCheckTypeSettings(d.checktypesettingslist || []);
    }
  }

  // ── helpers ───────────────────────────────────────────────
  const panLbl  = apiData.pantanvalidationlabel || 'Not Applicable';
  const industry = Array.isArray(apiData.industrytype) ? apiData.industrytype : [];

  const packageGroups = Object.values(
    pricingPackages.filter(p => p.pricingtype === 'Package').reduce((acc, p) => {
      const key = p.packageid;
      if (!acc[key]) acc[key] = { name: p.packagename, price: p.price, countryname: p.countryname, checks: [] };
      acc[key].checks.push(p);
      return acc;
    }, {})
  );

  const pricingAlacarteWithCat = pricingAlacarte.map(p => ({
    ...p,
    category: p.category || deriveCategory(p.checktype),
  }));
  const alacarteCategories = [...new Set(pricingAlacarteWithCat.map(p => p.category))].sort();
  const filteredAlacarte = pricingAlacarteWithCat.filter(p => {
    const matchSearch = !alacarteSearch || (p.checktype || '').toLowerCase().includes(alacarteSearch.toLowerCase());
    const matchCat = !alacarteCategory || p.category === alacarteCategory;
    return matchSearch && matchCat;
  });
  const CARD_PRIORITY = ['Address', 'Criminal', 'Education', 'Employment'];
  const catMap = pricingAlacarteWithCat.reduce((acc, p) => {
    const cat = p.category;
    if (!acc[cat]) acc[cat] = { count: 0, price: p.price, sublabels: [] };
    acc[cat].count++;
    const sub = getCheckTypeSubLabel(p.checktype, cat);
    if (sub && acc[cat].sublabels.length < 5) acc[cat].sublabels.push(sub);
    return acc;
  }, {});
  const topAlaCarteCats = [
    ...CARD_PRIORITY.filter(c => catMap[c]).map(c => [c, catMap[c]]),
    ...Object.entries(catMap).filter(([c]) => !CARD_PRIORITY.includes(c)).sort((a, b) => b[1].count - a[1].count),
  ].slice(0, 4);
  const totalPkgCheckTypes = new Set(packageGroups.flatMap(pkg => pkg.checks.map(c => c.checktype))).size;
  const totalPkgValue = packageGroups.reduce((sum, pkg) => sum + (parseFloat(pkg.price) || 0), 0);
  const extraCountries = [...new Set(pricingExtraChecks.map(p => p.countryname).filter(Boolean))];
  const extraCurrencies = [...new Set(pricingExtraChecks.map(p => p.currency).filter(Boolean))];

  function setT1F(k, v) { setT1(p => ({ ...p, [k]: v })); clearMsg(); }
  function setT2F(k, v) { setT2(p => ({ ...p, [k]: v })); setT2Errors(p => { const n = { ...p }; delete n[k]; return n; }); clearMsg(); }
  function setT4F(k, v) { setT4(p => ({ ...p, [k]: v })); clearMsg(); }
  function clearMsg()   { setPageMsg({ type: '', text: '' }); }

  function addContact()      { setContacts(p => [...p, emptyContact()]); }
  function removeContact(i)  { setContacts(p => p.filter((_, j) => j !== i)); }
  function setContact(i, k, v) { setContacts(p => p.map((c, j) => j === i ? { ...c, [k]: v } : c)); }

  function handleBillNA(field, checked) {
    setBillNA(p => ({ ...p, [field]: checked }));
    const key = `bill${field.charAt(0).toUpperCase() + field.slice(1)}`;
    setT2F(key, checked ? NOT_APPLICABLE_VALUE : '');
  }
  function handleShipNA(field, checked) {
    setShipNA(p => ({ ...p, [field]: checked }));
    const key = `ship${field.charAt(0).toUpperCase() + field.slice(1)}`;
    setT2F(key, checked ? NOT_APPLICABLE_VALUE : '');
  }

  function setAttachFile(type, file) {
    setAttachmentFiles(p => file ? { ...p, [type]: file } : (({ [type]: _, ...rest }) => rest)(p));
  }
  function toggleUpload(type, show) {
    setAttachmentUpload(p => ({ ...p, [type]: show }));
    if (!show) setAttachFile(type, null);
  }
  async function downloadExistingAttachment(fileName) {
    try {
      const { blob, filename } = await onboardingApi.downloadAttachment(fileName);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) {
      addToast('Download failed: ' + e.message, 'error');
    }
  }

  // ── Tab 1 submit ──────────────────────────────────────────
  async function submitTab1() {
    if (!t1.clientName.trim()) { addToast('Client Name is required', 'error'); return; }
    if (!contacts.some(c => c.contactType === 'Client POC')) {
      addToast('At least one Contact Person must be Client POC.', 'error'); return;
    }
    setSubmitting(true); clearMsg();
    try {
      const body = {
        SalesClient: {},
        SalesClientContact: {
          ClientId:               clientId ? Number(clientId) : 0,
          ContactType:  contacts.map(c => c.contactType  || ''),
          ContactName:  contacts.map(c => c.contactName  || ''),
          Designation:  contacts.map(c => c.designation  || ''),
          Email:        contacts.map(c => c.email        || ''),
          Phone:        contacts.map(c => c.phone        || ''),
          Mobile:       contacts.map(c => c.mobile       || ''),
        },
        ClientModel: {
          ClientId:               clientId ? Number(clientId) : 0,
          State:                  t1.state,
          Country:                t1.country,
          ClientType:             'PREPAID',
          CheckasperSLA:          '',
          ClientBillingInfoModel: { ClientType: 'PREPAID' },
          Clients: [{
            ClientName:    t1.clientName,
            CINnumber:     t1.cinNumber,
            City:          t1.city,
            Zipcode:       t1.zipCode,
            IndustryType:  t1.industryType,
            EmployeeSize:  t1.employeeSize,
            CheckasperSLA: '',
          }],
        },
      };
      const { ok, data } = await onboardingApi.saveContactDetails(body);
      if (ok) {
        gotoTab(LICLASS_TO_TAB[data.liclass] ?? 1);
        setPageMsg({ type: 'success', text: data.message || 'Client details saved successfully.' });
        loadTab(LICLASS_TO_TAB[data.liclass] ?? 1, false);
      } else {
        setPageMsg({ type: 'error', text: data.message || 'Failed to save. Please try again.' });
      }
    } finally { setSubmitting(false); }
  }

  // ── Tab 2 submit ──────────────────────────────────────────
  async function submitTab2() {
    clearMsg();
    const sa = t2.sameAsBill;
    const bG = billNA.gstin ? NOT_APPLICABLE_VALUE : t2.billGstin;
    const bP = billNA.pan   ? NOT_APPLICABLE_VALUE : t2.billPan;
    const bT = billNA.tan   ? NOT_APPLICABLE_VALUE : t2.billTan;
    const sG = sa ? bG : (shipNA.gstin ? NOT_APPLICABLE_VALUE : t2.shipGstin);
    const sP = sa ? bP : (shipNA.pan   ? NOT_APPLICABLE_VALUE : t2.shipPan);
    const sT = sa ? bT : (shipNA.tan   ? NOT_APPLICABLE_VALUE : t2.shipTan);

    const errors = {};
    if (!String(t2.attnPersonName || '').trim()) errors.attnPersonName = 'Required';
    if (!String(t2.department     || '').trim()) errors.department     = 'Required';
    if (!String(t2.billAddr1      || '').trim()) errors.billAddr1      = 'Required';
    if (!String(t2.billAddr2      || '').trim()) errors.billAddr2      = 'Required';
    if (!String(t2.billCountry    || '').trim()) errors.billCountry    = 'Required';
    if (!String(t2.billState      || '').trim()) errors.billState      = 'Required';
    if (!String(t2.billCity       || '').trim()) errors.billCity       = 'Required';
    if (!String(t2.billPostalCode || '').trim()) errors.billPostalCode = 'Required';
    if (!String(bG || '').trim()) errors.billGstin = 'Required';
    if (!String(bP || '').trim()) errors.billPan   = 'Required';
    if (!String(bT || '').trim()) errors.billTan   = 'Required';
    if (!sa) {
      if (!String(t2.shipAddr1      || '').trim()) errors.shipAddr1      = 'Required';
      if (!String(t2.shipAddr2      || '').trim()) errors.shipAddr2      = 'Required';
      if (!String(t2.shipCountry    || '').trim()) errors.shipCountry    = 'Required';
      if (!String(t2.shipState      || '').trim()) errors.shipState      = 'Required';
      if (!String(t2.shipCity       || '').trim()) errors.shipCity       = 'Required';
      if (!String(t2.shipPostalCode || '').trim()) errors.shipPostalCode = 'Required';
      if (!String(sG || '').trim()) errors.shipGstin = 'Required';
      if (!String(sP || '').trim()) errors.shipPan   = 'Required';
      if (!String(sT || '').trim()) errors.shipTan   = 'Required';
    }
    const GSTIN_RE = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}\d[Z]{1}[A-Z\d]{1}?$/;
    const PAN_RE   = /^[a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}$/;
    const TAN_RE   = /^[a-zA-Z\d]{10}$/;
    if (!errors.billGstin && !billNA.gstin && bG && bG !== NOT_APPLICABLE_VALUE && !GSTIN_RE.test(bG)) errors.billGstin = 'Invalid GSTIN format';
    if (!errors.billPan   && !billNA.pan   && bP && bP !== NOT_APPLICABLE_VALUE && !PAN_RE.test(bP))   errors.billPan   = 'Invalid PAN format';
    if (!errors.billTan   && !billNA.tan   && bT && bT !== NOT_APPLICABLE_VALUE && !TAN_RE.test(bT))   errors.billTan   = 'Invalid TAN format';
    if (!sa) {
      if (!errors.shipGstin && !shipNA.gstin && sG && sG !== NOT_APPLICABLE_VALUE && !GSTIN_RE.test(sG)) errors.shipGstin = 'Invalid GSTIN format';
      if (!errors.shipPan   && !shipNA.pan   && sP && sP !== NOT_APPLICABLE_VALUE && !PAN_RE.test(sP))   errors.shipPan   = 'Invalid PAN format';
      if (!errors.shipTan   && !shipNA.tan   && sT && sT !== NOT_APPLICABLE_VALUE && !TAN_RE.test(sT))   errors.shipTan   = 'Invalid TAN format';
    }
    if (Object.keys(errors).length > 0) { setT2Errors(errors); return; }
    setT2Errors({});

    setSubmitting(true);
    try {
      const fd = new FormData();
      const B = 'oClientBillingInfoModel.ClientBillingInfoModel';
      fd.append(`${B}.AttnPersonName`,  t2.attnPersonName);
      fd.append(`${B}.Department`,      t2.department);
      fd.append(`${B}.BillToAddress1`,  t2.billAddr1);
      fd.append(`${B}.BillToAddress2`,  t2.billAddr2);
      fd.append(`${B}.BillToAddress3`,  t2.billAddr3);
      fd.append(`${B}.BillToAddress4`,  t2.billAddr4);
      fd.append(`${B}.BillCountry`,     t2.billCountry);
      fd.append(`${B}.BillState`,       t2.billState);
      fd.append(`${B}.BillCity`,        t2.billCity);
      fd.append(`${B}.BillPostalCode`,  t2.billPostalCode);
      fd.append(`${B}.GSTINofBill`,     bG);
      fd.append(`${B}.PANofBill`,       bP);
      fd.append(`${B}.TANofBill`,       bT);
      fd.append(`${B}.ShipToAddress1`,  sa ? t2.billAddr1     : t2.shipAddr1);
      fd.append(`${B}.ShipToAddress2`,  sa ? t2.billAddr2     : t2.shipAddr2);
      fd.append(`${B}.ShipToAddress3`,  sa ? t2.billAddr3     : t2.shipAddr3);
      fd.append(`${B}.ShipToAddress4`,  sa ? t2.billAddr4     : t2.shipAddr4);
      fd.append(`${B}.ShipCountry`,     sa ? t2.billCountry   : t2.shipCountry);
      fd.append(`${B}.ShipState`,       sa ? t2.billState     : t2.shipState);
      fd.append(`${B}.ShipCity`,        sa ? t2.billCity      : t2.shipCity);
      fd.append(`${B}.ShipPostalCode`,  sa ? t2.billPostalCode : t2.shipPostalCode);
      fd.append(`${B}.GSTINofShip`,     sG);
      fd.append(`${B}.PANofShip`,       sP);
      fd.append(`${B}.TANofShip`,       sT);
      const { ok, data } = await onboardingApi.submitBillingInfo(
        { ClientId: clientId, BillingAction: t2.billingAction || 'Add' },
        fd,
      );
      if (ok) {
        if (data.ClientId) setClientId(String(data.ClientId));
        gotoTab(LICLASS_TO_TAB[data.liclass] ?? 2);
        setPageMsg({ type: 'success', text: data.message || 'Billing information saved successfully.' });
      } else {
        console.error('submitTab2 failed:', data);
        if (data.errors) console.error('Validation errors:', JSON.stringify(data.errors, null, 2));
        const errText = data.errors
          ? Object.entries(data.errors).map(([k, v]) => `${k}: ${v}`).join(' | ')
          : (data.message || data.title || 'Failed to save. Please try again.');
        setPageMsg({ type: 'error', text: errText });
      }
    } catch (e) {
      console.error('submitTab2 exception:', e);
      setPageMsg({ type: 'error', text: e.message || 'Failed to save. Please try again.' });
    } finally { setSubmitting(false); }
  }

  // ── Tab 4 download agreement ──────────────────────────────
  async function downloadAgreement() {
    setDownloading(true);
    try {
      const { blob, filename } = await onboardingApi.downloadAgreement();
      const url = URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) {
      addToast('Download failed: ' + e.message, 'error');
    } finally { setDownloading(false); }
  }

  // ── Tab 4 submit contract ─────────────────────────────────
  async function submitContract() {
    setSubmitting(true); clearMsg();
    try {
      // query-string params (no [FromForm] on these in the controller)
      const params = {
        ClientId:               clientId,
        ContractAction:         t4.contractAction,
        hdncontractcopy:        t4.hdncontractcopy,
        hdncontractcopyoriginal: t4.hdncontractcopyoriginal,
        hdncontractcopyext:     t4.hdncontractcopyext,
      };
      // form body: [FromForm] model fields + IFormFile attachments + [FromForm] arrays
      const fd = new FormData();
      fd.append('oClientContractModel.Id',           t4.contractId || 0);
      fd.append('oClientContractModel.ContractType', t4.contractType);
      fd.append('oClientContractModel.StartDate',    t4.startDate);
      if (t4.endDate) fd.append('oClientContractModel.EndDate', t4.endDate);
      fd.append('oClientContractModel.Remarks',      t4.remarks);
      fd.append('CFAttachment', CONTRACT_ATTACHMENTS.join(','));
      CONTRACT_ATTACHMENTS.forEach((type, i) => {
        const file     = attachmentFiles[type];
        const existing = existingAttachments.find(a => a.attachment_type === type);
        if (file) fd.append(`Attachment_${i}`, file);
        fd.append('ATTACHMENT_NAME', existing?.attachment_name || '');
        fd.append('ATTACHMENT_ID',   existing?.attachment_id || '');
      });
      const { ok, data } = await onboardingApi.submitContractInfo(params, fd);
      if (ok) {
        setPageMsg({ type: 'success', text: data.message || 'Contract submitted successfully.' });
        setCompleted(true);
        localStorage.removeItem('onb_clientId');
        localStorage.removeItem('onb_activeTab');
      } else {
        setPageMsg({ type: 'error', text: data.message || 'Failed to submit. Please try again.' });
      }
    } finally { setSubmitting(false); }
  }

  // ── render ────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--g50)' }}>
        <span className="spinner" style={{ width: 36, height: 36 }} />
      </div>
    );
  }

  return (
    <>
      <nav className="topnav">
        <div className="logo">
          <img src={cFirstLogo} alt="cFIRST BGV" style={{ height: '36px', width: 'auto' }} />
        </div>

        {!allDone && (
          <div style={{ fontSize: '12px', color: 'var(--g600)' }}>
            Complete all steps to activate your account
          </div>
        )}

        {/* Avatar + dropdown */}
        <div className="navr">
          <div style={{ position: 'relative' }} ref={menuRef}>
            <div className="av" onClick={() => setMenuOpen(o => !o)}>{initials}</div>

            {menuOpen && (
              <div style={{
                position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                background: 'var(--wh)', border: '1px solid var(--g200)',
                borderRadius: 10, boxShadow: '0 4px 24px rgba(0,0,0,.13)',
                minWidth: 230, zIndex: 300, overflow: 'hidden',
              }}>
                {/* Profile header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid var(--g100)', background: 'var(--g50)' }}>
                  <div className="av" style={{ flexShrink: 0, pointerEvents: 'none' }}>{initials}</div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--nv)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{companyName}</div>
                    {email && <div style={{ fontSize: 11, color: 'var(--g400)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 }}>{email}</div>}
                  </div>
                </div>

                <div style={{ borderTop: '1px solid var(--g100)' }}>
                  <OnbMenuItem icon="🚪" label="Logout" onClick={handleLogout} danger />
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      <div className="pg">
        <div className="phd">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div className="ptitle">Client Information</div>
              {status && <Chip label={status} />}
            </div>
            {!allDone && (
              <div className="psub">Please complete all steps to get started with cFIRST BGV</div>
            )}
          </div>
        </div>

        {allDone && (
          <div className="alert a-gr mb16">
            <div className="aico">✅</div>
            <div className="abody">Your onboarding is complete and currently under review. Our team will validate your account shortly — no further action is needed from your side.</div>
          </div>
        )}

        {pageMsg.text && (
          <div className={`alert ${pageMsg.type === 'success' ? 'a-gr' : 'a-rd'} mb16`}>
            <div className="aico">{pageMsg.type === 'success' ? '✅' : '⚠'}</div>
            <div className="abody">{pageMsg.text}</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', alignItems: 'start', gap: '20px' }}>

          {/* ── Left Nav ─────────────────────────────────── */}
          <div>
            <div className="snav">
              {TABS.map((t, i) => {
                const done   = completed || i < activeTab;
                const active = i === activeTab;
                return (
                  <div key={t.label} className={`sni ${active ? 'on' : ''}`} style={{ cursor: 'pointer' }} onClick={() => loadTab(i)}>
                    <span style={{ fontSize: '15px' }}>{t.icon}</span>
                    <span style={{ flex: 1 }}>{t.label}</span>
                    {done && <span style={{ fontSize: '10px', color: 'var(--gr)', fontWeight: 600 }}>Done</span>}
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: '12px', padding: '10px 12px', background: 'var(--or-xlt)', borderRadius: 'var(--rs)', border: '1px solid rgba(245,166,35,.25)', fontSize: '11.5px', color: 'var(--g600)' }}>
              <div style={{ fontWeight: 700, color: 'var(--or-dk)', marginBottom: '3px' }}>Step {activeTab + 1} of 4</div>
              <div>{TABS[activeTab]?.label}</div>
            </div>
          </div>

          {/* ── Right Content ─────────────────────────────── */}
          <div>

            {/* ════ Tab 1 — Client Contact Details ════ */}
            {activeTab === 0 && (
              <div className="scontent">
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--nv)', marginBottom: '16px' }}>Client Contact Details</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
                  <div className="fg">
                    <label className="flbl">Client Name <span className="req">*</span></label>
                    <input className="inp" value={t1.clientName} onChange={e => setT1F('clientName', e.target.value)} />
                  </div>
                  <div className="fg">
                    <label className="flbl">CIN Number</label>
                    <input className="inp" value={t1.cinNumber} onChange={e => setT1F('cinNumber', e.target.value)} />
                  </div>
                  <div className="fg">
                    <label className="flbl">Country <span className="req">*</span></label>
                    <select className="sel" value={t1.country} onChange={e => { setT1F('country', e.target.value); setT1F('state', ''); }}>
                      <option value="">-- Select Country --</option>
                      <option value="9999">Any Other Country</option>
                      {countries.map(c => <option key={c.value} value={c.value}>{c.text}</option>)}
                    </select>
                  </div>
                  <div className="fg">
                    <label className="flbl">State <span className="req">*</span></label>
                    <select className="sel" value={t1.state} onChange={e => setT1F('state', e.target.value)}>
                      <option value="">-- Select State --</option>
                      {t1States.map(s => <option key={s.value} value={s.value}>{s.text}</option>)}
                    </select>
                  </div>
                  <div className="fg">
                    <label className="flbl">City <span className="req">*</span></label>
                    <input className="inp" value={t1.city} onChange={e => setT1F('city', e.target.value)} />
                  </div>
                  <div className="fg">
                    <label className="flbl">Zip Code <span className="req">*</span></label>
                    <input className="inp" value={t1.zipCode} onKeyPress={onlyDigits} onChange={e => setT1F('zipCode', e.target.value)} />
                  </div>
                  <div className="fg">
                    <label className="flbl">Industry <span className="req">*</span></label>
                    <select className="sel" value={t1.industryType} onChange={e => setT1F('industryType', e.target.value)}>
                      <option value="">-- Select Industry --</option>
                      {industry.map(o => (
                        <option key={o.value ?? o.Value} value={o.value ?? o.Value}>{o.text ?? o.Text}</option>
                      ))}
                    </select>
                  </div>
                  <div className="fg">
                    <label className="flbl">Employee Size <span className="req">*</span></label>
                    <select className="sel" value={t1.employeeSize} onChange={e => setT1F('employeeSize', e.target.value)}>
                      {EMPLOYEE_SIZES.map(s => <option key={s} value={s}>{s || '-- Select Employee Size --'}</option>)}
                    </select>
                  </div>
                </div>

                {/* Contacts table */}
                <div style={{ marginTop: '22px' }}>
                  <div className="flex jb aic mb12">
                    <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--nv)' }}>Contact Persons</div>
                    <button className="btn bout bsm" onClick={addContact}>+ Add Contact</button>
                  </div>
                  <div className="twrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Contact Type</th>
                          <th>Contact Name</th>
                          <th>Designation</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Mobile</th>
                          <th style={{ width: 36 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts.map((c, i) => (
                          <tr key={i}>
                            <td>
                              <select className="fsel" style={{ minWidth: 120 }} value={c.contactType} onChange={e => setContact(i, 'contactType', e.target.value)}>
                                <option value="">Select</option>
                                {['Contact Person','Business Person','Cfirst Signing Authority','Client Signing Authority','Client POC','Client Finance POC','Client Invoicing Email'].map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </td>
                            <td><input className="inp" style={{ minWidth: 110 }} value={c.contactName} placeholder='Contact Name' onChange={e => setContact(i, 'contactName',  e.target.value)} /></td>
                            <td><input className="inp" style={{ minWidth: 100 }} value={c.designation} placeholder='Designation' onChange={e => setContact(i, 'designation',  e.target.value)} /></td>
                            <td><input className="inp" type="email" style={{ minWidth: 150 }} value={c.email} placeholder='Email' onChange={e => setContact(i, 'email', e.target.value)} /></td>
                            <td><input className="inp" style={{ minWidth: 90 }}  value={c.phone}  maxLength={13} placeholder='Phone No' onKeyPress={onlyDigits} onChange={e => setContact(i, 'phone',  e.target.value)} /></td>
                            <td><input className="inp" style={{ minWidth: 90 }}  value={c.mobile} maxLength={13} placeholder='Mobile No' onKeyPress={onlyDigits} onChange={e => setContact(i, 'mobile', e.target.value)} /></td>
                            <td>
                              {contacts.length > 1 && (
                                <button className="btn bghost bsm" style={{ color: 'var(--rd)', padding: '4px 8px' }} onClick={() => removeContact(i)}>✕</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="flex g8 mt16" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn bpri" onClick={submitTab1} disabled={submitting}>
                    {submitting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : 'Next →'}
                  </button>
                </div>
              </div>
            )}

            {/* ════ Tab 2 — Billing Info ════ */}
            {activeTab === 1 && (
              <div className="scontent">
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--nv)', marginBottom: '16px' }}>Billing Information</div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
                  <div className="fg">
                    <label className="flbl">Attn. Person Name <span className="req">*</span></label>
                    <input className="inp" value={t2.attnPersonName} onChange={e => setT2F('attnPersonName', e.target.value)} style={t2Errors.attnPersonName ? { borderColor: 'var(--rd)' } : {}} />
                    {t2Errors.attnPersonName && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{t2Errors.attnPersonName}</span>}
                  </div>
                  <div className="fg">
                    <label className="flbl">Department <span className="req">*</span></label>
                    <input className="inp" value={t2.department} onChange={e => setT2F('department', e.target.value)} style={t2Errors.department ? { borderColor: 'var(--rd)' } : {}} />
                    {t2Errors.department && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{t2Errors.department}</span>}
                  </div>
                </div>

                {/* Bill To */}
                <div style={{ marginTop: '20px', marginBottom: '8px', fontSize: '13px', fontWeight: 700, color: 'var(--nv)' }}>Bill To</div>
                <hr className="hr" style={{ margin: '0 0 12px 0' }} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 16px' }}>
                  <BillingField label="Address 1" req value={t2.billAddr1} onChange={v => setT2F('billAddr1', v)} error={t2Errors.billAddr1} />
                  <BillingField label="Address 2" req value={t2.billAddr2} onChange={v => setT2F('billAddr2', v)} error={t2Errors.billAddr2} />
                  <BillingField label="Address 3"     value={t2.billAddr3} onChange={v => setT2F('billAddr3', v)} />
                  <BillingField label="Address 4"     value={t2.billAddr4} onChange={v => setT2F('billAddr4', v)} />
                  <div className="fg">
                    <label className="flbl">Country <span className="req">*</span></label>
                    <select className="sel" value={t2.billCountry} onChange={e => { setT2F('billCountry', e.target.value); setT2F('billState', ''); }} style={t2Errors.billCountry ? { borderColor: 'var(--rd)' } : {}}>
                      <option value="">-- Select Country --</option>
                      <option value="9999">Any Other Country</option>
                      {countries.map(c => <option key={c.value} value={c.value}>{c.text}</option>)}
                    </select>
                    {t2Errors.billCountry && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{t2Errors.billCountry}</span>}
                  </div>
                  <div className="fg">
                    <label className="flbl">State <span className="req">*</span></label>
                    <select className="sel" value={t2.billState} onChange={e => setT2F('billState', e.target.value)} style={t2Errors.billState ? { borderColor: 'var(--rd)' } : {}}>
                      <option value="">-- Select State --</option>
                      {billStates.map(s => <option key={s.value} value={s.value}>{s.text}</option>)}
                    </select>
                    {t2Errors.billState && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{t2Errors.billState}</span>}
                  </div>
                  <BillingField label="City"        req value={t2.billCity}        onChange={v => setT2F('billCity', v)}        error={t2Errors.billCity} />
                  <BillingField label="Postal Code" req value={t2.billPostalCode}  onChange={v => setT2F('billPostalCode', v)}  error={t2Errors.billPostalCode} />
                  <NAField label="GSTIN" req value={t2.billGstin} naChecked={billNA.gstin} naLabel={panLbl}
                    onChange={v => setT2F('billGstin', v)} onNA={c => handleBillNA('gstin', c)} error={t2Errors.billGstin} />
                  <NAField label="PAN"   req value={t2.billPan}   naChecked={billNA.pan}   naLabel={panLbl}
                    onChange={v => setT2F('billPan', v)} onNA={c => handleBillNA('pan', c)} error={t2Errors.billPan} />
                  <NAField label="TAN"   req value={t2.billTan}   naChecked={billNA.tan}   naLabel={panLbl}
                    onChange={v => setT2F('billTan', v)} onNA={c => handleBillNA('tan', c)} error={t2Errors.billTan} />
                </div>

                {/* Ship To */}
                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--nv)' }}>Ship To</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--g600)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={t2.sameAsBill} onChange={e => setT2F('sameAsBill', e.target.checked)} />
                    Same as Bill To
                  </label>
                </div>
                <hr className="hr" style={{ margin: '0 0 12px 0' }} />
                {!t2.sameAsBill && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px 16px' }}>
                    <BillingField label="Address 1" req value={t2.shipAddr1} onChange={v => setT2F('shipAddr1', v)} error={t2Errors.shipAddr1} />
                    <BillingField label="Address 2" req value={t2.shipAddr2} onChange={v => setT2F('shipAddr2', v)} error={t2Errors.shipAddr2} />
                    <BillingField label="Address 3"     value={t2.shipAddr3} onChange={v => setT2F('shipAddr3', v)} />
                    <BillingField label="Address 4"     value={t2.shipAddr4} onChange={v => setT2F('shipAddr4', v)} />
                    <div className="fg">
                      <label className="flbl">Country <span className="req">*</span></label>
                      <select className="sel" value={t2.shipCountry} onChange={e => { setT2F('shipCountry', e.target.value); setT2F('shipState', ''); }} style={t2Errors.shipCountry ? { borderColor: 'var(--rd)' } : {}}>
                        <option value="">-- Select Country --</option>
                        <option value="9999">Any Other Country</option>
                        {countries.map(c => <option key={c.value} value={c.value}>{c.text}</option>)}
                      </select>
                      {t2Errors.shipCountry && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{t2Errors.shipCountry}</span>}
                    </div>
                    <div className="fg">
                      <label className="flbl">State <span className="req">*</span></label>
                      <select className="sel" value={t2.shipState} onChange={e => setT2F('shipState', e.target.value)} style={t2Errors.shipState ? { borderColor: 'var(--rd)' } : {}}>
                        <option value="">-- Select State --</option>
                        {shipStates.map(s => <option key={s.value} value={s.value}>{s.text}</option>)}
                      </select>
                      {t2Errors.shipState && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{t2Errors.shipState}</span>}
                    </div>
                    <BillingField label="City"        req value={t2.shipCity}        onChange={v => setT2F('shipCity', v)}        error={t2Errors.shipCity} />
                    <BillingField label="Postal Code" req value={t2.shipPostalCode}  onChange={v => setT2F('shipPostalCode', v)}  error={t2Errors.shipPostalCode} />
                    <NAField label="GSTIN" value={t2.shipGstin} naChecked={shipNA.gstin} naLabel={panLbl}
                      onChange={v => setT2F('shipGstin', v)} onNA={c => handleShipNA('gstin', c)} error={t2Errors.shipGstin} />
                    <NAField label="PAN"   value={t2.shipPan}   naChecked={shipNA.pan}   naLabel={panLbl}
                      onChange={v => setT2F('shipPan', v)} onNA={c => handleShipNA('pan', c)} error={t2Errors.shipPan} />
                    <NAField label="TAN"   value={t2.shipTan}   naChecked={shipNA.tan}   naLabel={panLbl}
                      onChange={v => setT2F('shipTan', v)} onNA={c => handleShipNA('tan', c)} error={t2Errors.shipTan} />
                  </div>
                )}

                <div className="flex g8 mt16" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn bghost" onClick={() => { gotoTab(0); clearMsg(); }}>← Back</button>
                  <button className="btn bpri" onClick={submitTab2} disabled={submitting}>
                    {submitting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Saving…</> : 'Next →'}
                  </button>
                </div>
              </div>
            )}

            {/* ════ Tab 3 — Pricing Info ════ */}
            {activeTab === 2 && (
              <div className="scontent">
                {/* Header */}
                <div className="flex jb aic" style={{ marginBottom: '16px' }}>
                  <div>
                    <div style={{ fontSize: '14.5px', fontWeight: 700, color: 'var(--nv)' }}>🏷️ Packages & Pricing</div>
                    <div style={{ fontSize: '12.5px', color: 'var(--g600)', marginTop: '2px' }}>Your configured check rates, packages and add-ons</div>
                  </div>
                </div>

                {/* Sub-tab bar */}
                <div className="tabbar" style={{ marginBottom: '20px' }}>
                  {PRICING_SUB_TABS.map((t, i) => (
                    <div key={t} className={`tab ${pricingSubTab === i ? 'on' : ''}`} onClick={() => { setPricingSubTab(i); setAlacarteSearch(''); setAlacarteCategory(''); }}>{t}</div>
                  ))}
                </div>

                {/* ── A la Carte ── */}
                {pricingSubTab === 0 && (
                  <>
                    {/* Category tier cards */}
                    {topAlaCarteCats.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '16px' }}>
                        {topAlaCarteCats.map(([cat, info]) => (
                          <div
                            key={cat}
                            onClick={() => setAlacarteCategory(c => c === cat ? '' : cat)}
                            style={{
                              border: `1.5px solid ${alacarteCategory === cat ? 'var(--or)' : 'var(--g200)'}`,
                              borderRadius: 'var(--r)', padding: '14px 15px', cursor: 'pointer',
                              background: alacarteCategory === cat ? 'var(--or-xlt)' : 'var(--wh)',
                              transition: 'all .15s',
                              boxShadow: alacarteCategory === cat ? '0 2px 10px rgba(245,166,35,.15)' : 'none',
                            }}
                          >
                            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--g400)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '5px' }}>
                              {info.count} check{info.count !== 1 ? 's' : ''}
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--nv)', marginBottom: '4px' }}>{cat}</div>
                            <div style={{ fontSize: '16px', fontWeight: 800, color: 'var(--or-dk)' }}>₹{info.price}</div>
                            {info.sublabels && info.sublabels.length > 0
                              ? (
                                <div style={{ fontSize: '11px', color: 'var(--g400)', marginTop: '5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  <div style={{ textTransform: 'capitalize' }}>{info.sublabels.map(s => s.toLowerCase()).slice(0, 3).join(', ')}{info.count > 3 ? '…' : ''}</div>
                                </div>
                              )
                              : (<div style={{ textTransform: 'capitalize', fontSize: '11px', color: 'var(--g400)', marginTop: '5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>-</div>)
                            }
                            <div style={{ marginTop: '8px' }}>
                              <span className={`chip ${catChipClass(cat)}`}>{cat}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Search + filter row */}
                    <div className="flex g8 aic" style={{ marginBottom: '16px' }}>
                      <div style={{ position: 'relative', flex: '0 1 240px' }}>
                        <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: 'var(--g400)', pointerEvents: 'none' }}>🔍</span>
                        <input
                          className="inp"
                          placeholder="Search check type…"
                          value={alacarteSearch}
                          onChange={e => setAlacarteSearch(e.target.value)}
                          style={{ paddingLeft: '30px' }}
                        />
                      </div>
                      {alacarteCategories.length > 0 && (
                        <select
                          className="sel"
                          value={alacarteCategory}
                          onChange={e => setAlacarteCategory(e.target.value)}
                          style={{ fontSize: '12px', width: 'auto' }}
                        >
                          <option value="">All Categories</option>
                          {alacarteCategories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      )}
                      <div style={{ fontSize: '11.5px', color: 'var(--g600)', whiteSpace: 'nowrap', padding: '0 4px' }}>
                        {filteredAlacarte.length} check{filteredAlacarte.length !== 1 ? 's' : ''}
                      </div>
                    </div>

                    {/* Table */}
                    <div className="twrap">
                      <table>
                        <thead>
                          <tr>
                            <th style={{ width: '42%' }}>Check Type</th>
                            <th>Price</th>
                            <th>Country</th>
                            <th>Currency</th>
                            {alacarteCategories.length > 0 && <th>Category</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAlacarte.length === 0 ? (
                            <tr>
                              <td colSpan={alacarteCategories.length > 0 ? 5 : 4} style={{ padding: '40px', textAlign: 'center' }}>
                                <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔍</div>
                                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--g600)' }}>
                                  {pricingAlacarteWithCat.length === 0 ? 'No A la Carte pricing configured yet.' : 'No checks found'}
                                </div>
                                {pricingAlacarteWithCat.length > 0 && <div style={{ fontSize: '11.5px', color: 'var(--g400)', marginTop: '4px' }}>Try a different search term</div>}
                              </td>
                            </tr>
                          ) : (
                            filteredAlacarte.map((p, i) => (
                              <tr key={i}>
                                <td className="td1" style={{ textTransform: 'capitalize' }}>{(p.checktype || '').toLowerCase() || '—'}</td>
                                <td>
                                  <span style={{
                                    display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
                                    background: 'var(--or-lt)', border: '1px solid rgba(245,166,35,.4)',
                                    borderRadius: '50px', fontSize: '12px', fontWeight: 700, color: 'var(--or-dk)',
                                  }}>
                                    ₹{p.price || '—'}
                                  </span>
                                </td>
                                <td style={{ fontSize: '12px', color: 'var(--g600)' }}>{p.countryname || '—'}</td>
                                <td>
                                  <span style={{
                                    display: 'inline-block', background: 'var(--g100)', border: '1px solid var(--g200)',
                                    color: 'var(--g600)', fontSize: '10.5px', fontWeight: 600,
                                    padding: '2px 8px', borderRadius: '4px', fontFamily: 'monospace',
                                  }}>
                                    {p.currency || '—'}
                                  </span>
                                </td>
                                {alacarteCategories.length > 0 && (
                                  <td>
                                    {p.category
                                      ? <span className={`chip ${catChipClass(p.category)}`}>{p.category}</span>
                                      : <span style={{ color: 'var(--g400)', fontSize: '12px' }}>—</span>}
                                  </td>
                                )}
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div style={{ borderTop: '1px solid var(--g100)', margin: '14px 0' }} />
                    <div className="alert a-or">
                      <div className="aico">ℹ️</div>
                      <div className="abody">
                        <div style={{ fontWeight: 700, color: 'var(--nv)', marginBottom: '2px', fontSize: '12px' }}>Pricing configured by cFIRST</div>
                        Rates above reflect your negotiated contract pricing. Contact your account manager for any revisions.
                      </div>
                    </div>
                  </>
                )}

                {/* ── Package ── */}
                {pricingSubTab === 1 && (
                  <>
                    {/* Stat cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '18px' }}>
                      {[
                        { icon: '📦', bg: 'var(--or-lt)', label: 'Total packages', val: packageGroups.length, note: 'Active' },
                        { icon: '✅', bg: 'var(--gr-lt)', label: 'Check types',     val: totalPkgCheckTypes, note: 'Across all packages' },
                        { icon: '💳', bg: 'var(--bl-lt)', label: 'Total value',     val: `₹${totalPkgValue.toLocaleString('en-IN')}`, note: 'Combined' },
                      ].map(s => (
                        <div key={s.label} style={{ background: 'var(--g50)', border: '1px solid var(--g200)', borderRadius: 'var(--rs)', padding: '13px 15px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: 38, height: 38, borderRadius: 9, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{s.icon}</div>
                          <div>
                            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--g600)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '2px' }}>{s.label}</div>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--nv)', lineHeight: 1 }}>{s.val}</div>
                            <div style={{ fontSize: '10.5px', color: 'var(--g400)', marginTop: '2px' }}>{s.note}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Package cards */}
                    {packageGroups.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--g400)' }}>
                        <div style={{ fontSize: '32px', marginBottom: '8px' }}>📦</div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--g600)' }}>No packages configured</div>
                        <div style={{ fontSize: '11.5px', marginTop: '4px' }}>Contact your account manager to set up packages</div>
                      </div>
                    ) : (
                      packageGroups.map((pkg, pi) => (
                        <div key={pi} style={{ border: '1px solid var(--g200)', borderRadius: 'var(--r)', overflow: 'hidden', marginBottom: '14px' }}>
                          {/* Package card header */}
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '13px 18px',
                            background: 'linear-gradient(90deg, var(--g50) 0%, var(--or-xlt) 100%)',
                            borderBottom: '1px solid var(--g200)',
                          }}>
                            <div>
                              <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--nv)' }}>{pkg.name || '—'}</div>
                              <div style={{ fontSize: '11px', color: 'var(--g400)', marginTop: '2px' }}>
                                {pkg.checks.length} check type{pkg.checks.length !== 1 ? 's' : ''} · {pkg.countryname || 'India'}
                              </div>
                            </div>
                            <div className="flex aic g8">
                              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--g600)', background: 'var(--wh)', border: '1px solid var(--g200)', borderRadius: '50px', padding: '2px 10px' }}>
                                🌏 {pkg.countryname || 'India'}
                              </span>
                              <span className="chip cg">Active</span>
                              <span style={{ fontSize: '16px', fontWeight: 800, color: 'var(--or-dk)' }}>₹{pkg.price || '—'}</span>
                            </div>
                          </div>

                          {/* Checks table */}
                          <div className="twrap">
                            <table>
                              <thead>
                                <tr>
                                  <th style={{ width: '40%' }}>Check Type</th>
                                  <th>Qty</th>
                                </tr>
                              </thead>
                              <tbody>
                                {pkg.checks.map((c, ki) => {
                                  const ctSetting = checkTypeSettings.find(ct => ct.checktypelbl === c.checktype);
                                  const hasTenure = c.has_tenure === 'TRUE' || ctSetting?.has_tenure === 'TRUE';
                                  return (
                                    <tr key={ki}>
                                      <td className="td1" style={{ textTransform: 'capitalize' }}>{(c.checktype || '').toLowerCase() || '—'}</td>
                                      <td>
                                        <QtyBubble val={c.chktypepkgcount} />
                                        {hasTenure && <TenureChip />}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))
                    )}

                    <div className="alert a-bl" style={{ marginTop: '12px' }}>
                      <div className="aico">ℹ️</div>
                      <div className="abody">
                        <div style={{ fontWeight: 700, color: 'var(--nv)', marginBottom: '2px', fontSize: '12px' }}>Tenure-based checks</div>
                        Checks marked <strong>Is Tenure</strong> are unlimited within your contract tenure period — they do not deduct per-use credits.
                      </div>
                    </div>
                  </>
                )}

                {/* ── Extra Checks ── */}
                {pricingSubTab === 2 && (
                  <>
                    {/* Stat cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '18px' }}>
                      {[
                        { icon: '➕', bg: 'var(--or-lt)', label: 'Extra checks', val: pricingExtraChecks.length, note: 'Add-on types' },
                        { icon: '🌍', bg: '#e0f2fe',      label: 'Countries',    val: extraCountries.length || '—', note: extraCountries.length > 0 ? `${extraCountries[0]}${extraCountries.length > 1 ? ` +${extraCountries.length - 1}` : ''}` : 'Available' },
                        { icon: '💱', bg: 'var(--bl-lt)', label: 'Currency',     val: extraCurrencies[0] || '—', note: extraCurrencies.length > 1 ? `+ ${extraCurrencies.length - 1} more` : 'Indian Rupee' },
                      ].map(s => (
                        <div key={s.label} style={{ background: 'var(--g50)', border: '1px solid var(--g200)', borderRadius: 'var(--rs)', padding: '13px 15px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{ width: 38, height: 38, borderRadius: 9, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{s.icon}</div>
                          <div>
                            <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--g600)', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '2px' }}>{s.label}</div>
                            <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--nv)', lineHeight: 1 }}>{s.val}</div>
                            <div style={{ fontSize: '10.5px', color: 'var(--g400)', marginTop: '2px' }}>{s.note}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="twrap">
                      <table>
                        <thead>
                          <tr>
                            <th style={{ width: '42%' }}>Check Type</th>
                            <th>Price</th>
                            <th>Country</th>
                            <th>Currency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pricingExtraChecks.length === 0 ? (
                            <tr>
                              <td colSpan={4} style={{ textAlign: 'center', padding: '28px', color: 'var(--g400)' }}>No extra checks configured yet.</td>
                            </tr>
                          ) : (
                            pricingExtraChecks.map((p, i) => (
                              <tr key={i}>
                                <td className="td1" style={{ textTransform: 'capitalize' }}>{(p.extracheckname || '').toLowerCase() || '—'}</td>
                                <td>
                                  <span style={{
                                    display: 'inline-flex', alignItems: 'center', padding: '3px 10px',
                                    background: 'var(--or-lt)', border: '1px solid rgba(245,166,35,.4)',
                                    borderRadius: '50px', fontSize: '12px', fontWeight: 700, color: 'var(--or-dk)',
                                  }}>
                                    ₹{p.price || '—'}
                                  </span>
                                </td>
                                <td style={{ fontSize: '12px', color: 'var(--g600)' }}>{p.countryname || '—'}</td>
                                <td>
                                  <span style={{
                                    display: 'inline-block', background: 'var(--g100)', border: '1px solid var(--g200)',
                                    color: 'var(--g600)', fontSize: '10.5px', fontWeight: 600,
                                    padding: '2px 8px', borderRadius: '4px', fontFamily: 'monospace',
                                  }}>
                                    {p.currency || '—'}
                                  </span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div style={{ borderTop: '1px solid var(--g100)', margin: '14px 0' }} />
                    <div className="alert a-bl">
                      <div className="aico">ℹ️</div>
                      <div className="abody">
                        <div style={{ fontWeight: 700, color: 'var(--nv)', marginBottom: '2px', fontSize: '12px' }}>Pricing is configured by cFIRST</div>
                        Extra checks are add-ons outside your standard package. Contact your account manager to add more check types or adjust pricing.
                      </div>
                    </div>
                  </>
                )}

                <div className="flex g8 mt16" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn bghost" onClick={() => { gotoTab(1); clearMsg(); }}>← Back</button>
                  <button className="btn bpri" onClick={() => { gotoTab(3); clearMsg(); }}>Next →</button>
                </div>
              </div>
            )}

            {/* ════ Tab 4 — Contract ════ */}
            {activeTab === 3 && (
              <div className="scontent">
                <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--nv)', marginBottom: '16px' }}>Contract</div>

                {/* Download agreement */}
                <div className="flex jb aic mt20" style={{ background: 'var(--g50)', borderRadius: 'var(--rs)', padding: '12px 14px', border: '1px solid var(--g200)' }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '13px' }}>Prepaid Agreement</div>
                    <div style={{ fontSize: '11.5px', color: 'var(--g600)', marginTop: '2px' }}>Download, review and sign the agreement before submitting</div>
                  </div>
                  <button className="btn bout" onClick={downloadAgreement} disabled={downloading}>
                    {downloading ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Downloading…</> : '⬇ Download Agreement'}
                  </button>
                </div>

                {/* Attachments */}
                <div style={{ marginTop: '20px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--nv)', marginBottom: '12px' }}>Attachments</div>
                  <div style={{ border: '1px solid var(--g200)', borderRadius: 'var(--rs)', overflow: 'hidden' }}>
                    {CONTRACT_ATTACHMENTS.map((type, i) => {
                      const existing    = existingAttachments.find(a => a.attachment_type === type);
                      const inUpload    = !existing || !!attachmentUpload[type];
                      const selectedFile = attachmentFiles[type];
                      return (
                        <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', borderBottom: i < CONTRACT_ATTACHMENTS.length - 1 ? '1px solid var(--g100)' : 'none', background: i % 2 === 0 ? 'var(--g50)' : '' }}>
                          <div style={{ width: '140px', fontSize: '13px', fontWeight: 600, color: 'var(--g700)', flexShrink: 0 }}>{type}</div>
                          {inUpload ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                              <label className="btn bout bsm" style={{ cursor: 'pointer', margin: 0, maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {selectedFile ? selectedFile.name : 'Select File'}
                                <input type="file" style={{ display: 'none' }} onChange={e => { setAttachFile(type, e.target.files[0] || null); e.target.value = ''; }} />
                              </label>
                              {existing && (
                                <button type="button" className="btn bghost bsm" onClick={() => toggleUpload(type, false)}>Cancel</button>
                              )}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                              <button type="button" className="btn bout bsm" onClick={() => downloadExistingAttachment(existing.attachment_name)}>
                                ⬇ {existing.attachment_name}
                              </button>
                              <button type="button" className="btn bghost bsm" onClick={() => toggleUpload(type, true)}>
                                Change Document
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex g8 mt20" style={{ justifyContent: 'flex-end' }}>
                  <button className="btn bghost" onClick={() => { gotoTab(2); clearMsg(); }}>← Back</button>
                  <button className="btn bpri" onClick={submitContract} disabled={submitting}>
                    {submitting ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Submitting…</> : '✓ Finish'}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}

// ── Small sub-components ──────────────────────────────────

function BillingField({ label, req, value, onChange, error }) {
  return (
    <div className="fg">
      <label className="flbl">{label} {req && <span className="req">*</span>}</label>
      <input className="inp" value={value} onChange={e => onChange(e.target.value)}
        style={error ? { borderColor: 'var(--rd)' } : {}} />
      {error && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{error}</span>}
    </div>
  );
}

function NAField({ label, req, value, naChecked, naLabel, onChange, onNA, error }) {
  return (
    <div className="fg">
      <label className="flbl">{label} {req && <span className="req">*</span>}</label>
      <input className="inp" value={value} onChange={e => onChange(e.target.value)} disabled={naChecked}
        style={{ ...(naChecked ? { background: 'var(--g100)', color: 'var(--g400)' } : {}), ...(error && !naChecked ? { borderColor: 'var(--rd)' } : {}) }} />
      {error && !naChecked && <span style={{ fontSize: '11px', color: 'var(--rd)', display: 'block', marginTop: '2px' }}>{error}</span>}
      <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: 'var(--g600)', cursor: 'pointer', marginTop: '4px' }}>
        <input type="checkbox" checked={naChecked} onChange={e => onNA(e.target.checked)} />
        {naLabel}
      </label>
    </div>
  );
}

function QtyBubble({ val }) {
  const n = parseInt(val, 10);
  const hasVal = !isNaN(n) && n > 0;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      width: 24, height: 24, borderRadius: '50%', fontSize: '11px', fontWeight: 700,
      background: hasVal ? 'var(--or)' : 'var(--g100)',
      border: hasVal ? 'none' : '1px solid var(--g200)',
      color: hasVal ? 'var(--nv)' : 'var(--g400)',
    }}>
      {val != null ? val : '—'}
    </span>
  );
}

function TenureChip() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '4px',
      fontSize: '10px', fontWeight: 600,
      background: 'var(--pu-lt)', color: '#5B21B6',
      border: '1px solid rgba(124,58,237,.2)',
      borderRadius: '50px', padding: '2px 7px', marginLeft: '5px',
    }}>
      🕐 yrs
    </span>
  );
}

function OnbMenuItem({ icon, label, onClick, danger }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: '10px 16px', fontSize: 13, cursor: 'pointer',
        color: danger ? 'var(--rd)' : 'var(--nv)',
        fontWeight: danger ? 600 : 400,
        background: hovered ? (danger ? 'rgba(220,38,38,.05)' : 'var(--g50)') : 'transparent',
        transition: 'background .12s',
      }}
    >
      <span style={{ fontSize: 14 }}>{icon}</span>
      {label}
    </div>
  );
}
